/**
 * AdSlot — an in-content ad container.
 *
 * Two build-time switches keep it inert until AdSense approves the site:
 *   VITE_ADSENSE_CLIENT  the ca-pub-… publisher ID (also adds the head loader)
 *   VITE_ADSENSE_SLOTS   set to "on" after approval to render real ad units
 *
 * Real ad units need numeric slot IDs from the AdSense dashboard, so slotId
 * must be one of those, not a descriptive name.
 */
import { useEffect } from 'react';

type Props = { slotId: string; format?: 'fluid' | 'rectangle' | 'horizontal' };

const ADSENSE_CLIENT = import.meta.env.VITE_ADSENSE_CLIENT as string | undefined;
const ADS_ACTIVE = Boolean(ADSENSE_CLIENT) && import.meta.env.VITE_ADSENSE_SLOTS === 'on';

export function AdSlot({ slotId, format = 'fluid' }: Props) {
  useEffect(() => {
    if (!ADS_ACTIVE) return;
    try {
      const w = window as unknown as { adsbygoogle?: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      /* ad script blocked or not loaded — the slot simply stays empty */
    }
  }, [slotId]);

  if (!ADS_ACTIVE) {
    // Dev only: a dashed placeholder keeps layout shift visible locally.
    if (!import.meta.env.DEV) return null;
    return (
      <aside
        aria-label="Sponsored slot (inactive in development)"
        className="my-8 rounded-card border border-dashed border-ink-300 bg-ink-50 px-4 py-6 text-center text-xs text-ink-400"
        data-slot-id={slotId}
      >
        Ad slot · {slotId} · {format}
      </aside>
    );
  }
  return (
    <aside className="my-8" aria-label="Sponsored content" data-slot-id={slotId}>
      <ins
        className="adsbygoogle block"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </aside>
  );
}
